import { yupResolver } from "@hookform/resolvers/yup";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import FormWrapper from "src/components/FormWrapper";
import { FormProvider, RHFTextField, RHFSelect } from "src/components/hook-form";
import { saveChecklist, getChecklistById, getAllChecklistCategories, updateChecklistAssignment } from "src/pages/api";
import Iconify from "src/components/Iconify";
import * as Yup from "yup";

function normalizeFieldType(ft) {
  if (!ft) return "TextBox";
  const map = {
    dropdown: "Dropdown",
    "drop down": "Dropdown",
    checkbox: "Checkbox",
    radio: "Radio Button",
    "radio button": "Radio Button",
    text: "TextBox",
    textbox: "TextBox",
    embedvideo: "Embed Video",
    "embed video": "Embed Video",
    date: "Date",
  };
  return map[ft.toLowerCase()] ?? ft;
}

const FIELD_TYPE_MAP = {
  TextBox: "text",
  Dropdown: "dropdown",
  Checkbox: "checkbox",
  "Radio Button": "radio",
  Date: "date",
  "Embed Video": "embedVideo",
};

function needsOptions(ft) {
  return ["Dropdown", "Checkbox", "Radio Button"].includes(ft);
}

function FieldPreview({ fieldType, optionsText }) {
  const options = String(optionsText || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  if (!options.length) return null;

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        borderRadius: 1,
        bgcolor: "grey.100",
        border: "1px dashed",
        borderColor: "grey.400",
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
        Preview
      </Typography>

      {fieldType === "Dropdown" && (
        <FormControl size="small" fullWidth>
          <Select displayEmpty defaultValue="" size="small">
            <MenuItem value="" disabled>Select an option</MenuItem>
            {options.map((opt, i) => (
              <MenuItem key={i} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {fieldType === "Radio Button" && (
        <FormControl>
          <FormLabel sx={{ fontSize: 12 }}>Options</FormLabel>
          <RadioGroup>
            {options.map((opt, i) => (
              <FormControlLabel
                key={i}
                value={opt}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{opt}</Typography>}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      {fieldType === "Checkbox" && (
        <FormControl>
          <FormLabel sx={{ fontSize: 12 }}>Options</FormLabel>
          <Stack>
            {options.map((opt, i) => (
              <FormControlLabel
                key={i}
                control={<Checkbox size="small" />}
                label={<Typography variant="body2">{opt}</Typography>}
              />
            ))}
          </Stack>
        </FormControl>
      )}
    </Box>
  );
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    // youtu.be/ID
    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    // youtube.com/watch?v=ID
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
      // already an embed URL
      if (u.pathname.startsWith("/embed/")) return url;
    }
  } catch {
    // not a valid URL
  }
  return null;
}

function VideoPreview({ url }) {
  const embedUrl = getYouTubeEmbedUrl(url);
  if (!embedUrl) return null;
  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        borderRadius: 1,
        bgcolor: "grey.100",
        border: "1px dashed",
        borderColor: "grey.400",
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
        Preview
      </Typography>
      <Box sx={{ position: "relative", paddingTop: "56.25%", borderRadius: 1, overflow: "hidden" }}>
        <iframe
          src={embedUrl}
          title="Video preview"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
        />
      </Box>
    </Box>
  );
}

const REPEAT_OPTIONS = [
  { value: "Does not repeat", label: "Does not repeat" },
  { value: "Daily", label: "Daily" },
  { value: "Weekly", label: "Weekly" },
  { value: "Monthly", label: "Monthly" },
];

const formatTimeTo12h = (time24) => {
  if (!time24) return null;
  const [hours, minutes] = time24.split(":");
  const h = parseInt(hours, 10);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, "0")}:${minutes} ${suffix}`;
};

const formatTimeTo24h = (time12) => {
  if (!time12) return "";
  const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return "";
  let h = parseInt(match[1], 10);
  const m = match[2];
  const suffix = match[3].toUpperCase();
  if (suffix === "AM" && h === 12) h = 0;
  if (suffix === "PM" && h !== 12) h += 12;
  return `${String(h).padStart(2, "0")}:${m}`;
};

const defaultValues = {
  name: "",
  categoryId: "",
  type: "",
  notifyEmails: "",
  requireSignature: false,
  fields: [],
};

const schema = Yup.object().shape({
  name: Yup.string().trim().required("Checklist name is required"),
  categoryId: Yup.string().trim().required("Category is required"),
  type: Yup.string().trim().required("Type is required"),
  notifyEmails: Yup.string()
    .trim()
    .nullable()
    .test("emails-valid", "Enter valid email address(es), comma separated", (value) => {
      if (!value) return true;
      return value
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean)
        .every((e) => Yup.string().email().isValidSync(e));
    }),
  requireSignature: Yup.boolean(),
  fields: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().nullable(),
      fieldName: Yup.string().required("Field name is required"),
      fieldType: Yup.string().required("Field type is required"),
      imageRequired: Yup.boolean(),
      optionsText: Yup.string().nullable(),
      videoLink: Yup.string().nullable().default(""),
      imageCount: Yup.number().nullable(),
    })
  ),
});

export default function AddChecklist({ onClose, row, isEditMode }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // assignments from API response
  const [assignments, setAssignments] = useState([]);
  // which assignmentId is being edited (inline)
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  // per-assignment reminder form state: { date, time, repeat, noExpiry }
  const [reminderForms, setReminderForms] = useState({});
  const [reminderErrors, setReminderErrors] = useState({});

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { handleSubmit, reset, setValue, watch, control } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: "fields" });
  const currentFields = watch("fields") || [];

  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery({
    queryKey: ["checklist-categories", 0, 100, ""],
    queryFn: getAllChecklistCategories,
  });
  const categories = categoriesData?.data || [];

  const { isLoading: isLoadingChecklist } = useQuery({
    queryKey: ["checklist-detail", row?.id],
    queryFn: ({ signal }) => getChecklistById({ id: row.id, signal }),
    enabled: isEditMode && !!row?.id,
    onSuccess: (data) => {
      if (data) {
        const checklist = data.data || data;
        const transformedFields = (checklist.fields || []).map((field) => {
          const normalized = normalizeFieldType(field.fieldType);
          return {
            id: field.id ?? null,
            fieldName: field.name || field.fieldName || "",
            fieldType: normalized,
            imageRequired: field.imageRequired || false,
            imageCount: field.imageCount || 3,
            optionsText: normalized === "Embed Video" ? "" : (field.options || []).join(", "),
            videoLink: normalized === "Embed Video" ? (field.options?.[0] || "") : "",
          };
        });
        reset({
          name: checklist.name || "",
          categoryId: checklist.categoryId || checklist.category?.id || "",
          type: checklist.type || "",
          notifyEmails: checklist.notifyEmails || "",
          requireSignature: checklist.requireSignature === 1 || checklist.requireSignature === true,
          fields: transformedFields,
        });

        // Store assignments and seed reminder forms
        const asgns = checklist.assignments || [];
        setAssignments(asgns);
        const forms = {};
        asgns.forEach((a) => {
          forms[a.id] = {
            date: a.startDate || "",
            time: formatTimeTo24h(a.time),
            repeat: a.reminder || "Does not repeat",
            repeatEvery: a.repeat != null ? String(a.repeat) : "",
            perday: a.perday != null ? String(a.perday) : "",
            noExpiry: a.expiry === 0,
          };
        });
        setReminderForms(forms);
      }
    },
    onError: () => {
      enqueueSnackbar("Failed to load checklist details", { variant: "error" });
    },
  });

  const mutation = useMutation(
    async (formData) => {
      const transformedFields = (formData.fields || []).map((field) => {
        const isVideo = field.fieldType === "Embed Video";
        const rawOptions = isVideo
          ? (field.videoLink?.trim() ? [field.videoLink.trim()] : [])
          : String(field.optionsText || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);

        return {
          id: field.id,
          name: field.fieldName,
          fieldType: FIELD_TYPE_MAP[field.fieldType] || field.fieldType,
          imageRequired: !!field.imageRequired,
          imageCount: field.imageRequired ?  3 : 0,
          options: rawOptions,
        };
      });

      const payload = {
        name: formData.name?.trim(),
        categoryId: formData.categoryId,
        type: formData.type,
        notifyEmails: String(formData.notifyEmails || "")
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean)
          .join(","),
        requireSignature: formData.requireSignature ? 1 : 0,
        fields: transformedFields,
      };

      return saveChecklist({ id: isEditMode && row?.id ? row.id : undefined, req: payload });
    },
    {
      onSuccess: () => {
        enqueueSnackbar(
          `Checklist ${isEditMode ? "updated" : "created"} successfully`,
          { variant: "success" }
        );
        queryClient.invalidateQueries(["checklists"]);
        queryClient.invalidateQueries(["checklist-detail"]);
        onClose();
      },
      onError: (error) => {
        const apiMessage =
          error?.response?.data?.details ||
          error?.response?.data?.error ||
          error?.response?.data?.message;
        enqueueSnackbar(
          apiMessage || error?.message || "Failed to save checklist",
          { variant: "error" }
        );
      },
    }
  );

  const updateAssignmentMutation = useMutation(
    async ({ assignmentId, startDate, time, repeat, repeatEvery, perday, noExpiry, removeAssignment }) => {
      if (removeAssignment) {
        return updateChecklistAssignment({ assignmentId, remove: true });
      }
      if (!startDate) throw new Error("Date is required");
      if (!time) throw new Error("Time is required");
      if (!repeat) throw new Error("Repeat is required");
      return updateChecklistAssignment({
        assignmentId,
        startDate,
        time: formatTimeTo12h(time),
        reminder: repeat,
        repeat: repeatEvery ? Number(repeatEvery) : undefined,
        perday: perday ? Number(perday) : undefined,
        expiry: noExpiry ? 0 : 1,
      });
    },
    {
      onSuccess: (_, { assignmentId, removeAssignment }) => {
        if (removeAssignment) {
          // Remove assignment from local state
          setAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
          setReminderForms((prev) => {
            const newForms = { ...prev };
            delete newForms[assignmentId];
            return newForms;
          });
          setReminderErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[assignmentId];
            return newErrors;
          });
          if (editingAssignmentId === assignmentId) {
            setEditingAssignmentId(null);
          }
          enqueueSnackbar("Assignment removed successfully", { variant: "success" });
        } else {
          enqueueSnackbar("Reminder updated successfully", { variant: "success" });
          setEditingAssignmentId(null);
          setReminderErrors((prev) => ({ ...prev, [assignmentId]: null }));
        }
        queryClient.invalidateQueries(["checklists"]);
        queryClient.invalidateQueries(["checklist-detail", row?.id]);
      },
      onError: (error, { assignmentId }) => {
        const apiMessage =
          error?.response?.data?.details ||
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message;
        setReminderErrors((prev) => ({ ...prev, [assignmentId]: apiMessage }));
        enqueueSnackbar(apiMessage || "Failed to update reminder", { variant: "error" });
      },
    },
  );

  const handleDeleteAssignment = (assignmentId) => {
    updateAssignmentMutation.mutate({ assignmentId, removeAssignment: true });
  };

  useEffect(() => {
    if (!isEditMode) reset(defaultValues);
  }, [isEditMode, reset]);

  const fieldTypeOptions = [
    { value: "TextBox", label: "Text Box" },
    { value: "Dropdown", label: "Dropdown" },
    { value: "Checkbox", label: "Checkbox" },
    { value: "Radio Button", label: "Radio Button" },
    { value: "Date", label: "Date" },
    { value: "Embed Video", label: "Embed Video" },
  ];

  const typeOptions = [
    { value: "Open with Only QR Code", label: "Open with Only QR Code" },
    { value: "Open without QR Code", label: "Open without QR Code" },
  ];

  return (
    <FormWrapper
      onClose={onClose}
      onSubmit={handleSubmit((d) => mutation.mutate(d))}
      onReset={() => reset(defaultValues)}
      title={isEditMode ? "Update Checklist" : "Create Checklist"}
      loading={mutation.isLoading || isLoadingChecklist}
      isEditMode={isEditMode}
      maxWidth="md"
    >
      <FormProvider methods={methods} onSubmit={handleSubmit((d) => mutation.mutate(d))}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <RHFTextField
              name="name"
              label="Checklist Name"
              required
              disabled={isLoadingChecklist}
              placeholder="Enter checklist name"
            />
          </Grid>

          <Grid item xs={12}>
            <RHFSelect
              name="categoryId"
              label="Category"
              required
              disabled={isLoadingChecklist || isLoadingCategories}
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </RHFSelect>
          </Grid>

          <Grid item xs={12}>
            <RHFSelect name="type" label="Type" required disabled={isLoadingChecklist}>
              <option value="">Select Type</option>
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </RHFSelect>
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="notifyEmails"
              control={control}
              render={({ field, fieldState: { error } }) => {
                const emails = String(field.value || "")
                  .split(",")
                  .map((e) => e.trim())
                  .filter(Boolean);

                return (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={emails}
                    disabled={isLoadingChecklist}
                    onChange={(_, newValue) =>
                      field.onChange(
                        newValue
                          .map((e) => String(e).trim())
                          .filter(Boolean)
                          .join(", "),
                      )
                    }
                    onBlur={(e) => {
                      const pending = e.target.value?.trim();
                      if (pending) {
                        field.onChange([...emails, pending].join(", "));
                      }
                      field.onBlur();
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Notify Emails"
                        placeholder={emails.length ? "" : "e.g. abc@example.com"}
                        error={!!error}
                        helperText={error?.message || "Press Enter or comma to add an email"}
                      />
                    )}
                  />
                );
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!watch("requireSignature")}
                  onChange={(e) => setValue("requireSignature", e.target.checked)}
                  disabled={isLoadingChecklist}
                  color="primary"
                />
              }
              label="Require Signature"
            />
          </Grid>

          {/* Assignments / Reminder section - edit mode only */}
          {isEditMode && assignments.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                Assigned Clients - Update Reminder
              </Typography>
              <Stack spacing={1.5}>
                {assignments.map((a) => {
                  const isOpen = editingAssignmentId === a.id;
                  const form = reminderForms[a.id] || {};
                  const errMsg = reminderErrors[a.id];
                  const isWithoutQR = watch("type") === "Open without QR Code";

                  const setForm = (patch) =>
                    setReminderForms((prev) => ({
                      ...prev,
                      [a.id]: { ...prev[a.id], ...patch },
                    }));

                  return (
                    <Card key={a.id} variant="outlined" sx={{ p: 1.5, backgroundColor: "#FAFAFA" }}>
                      {/* Header row */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                      >
                        {/* Client info */}
                        <Box
                          sx={{ cursor: isWithoutQR ? "default" : "pointer", flex: 1 }}
                          onClick={() => !isWithoutQR && setEditingAssignmentId(isOpen ? null : a.id)}
                        >
                          <Typography variant="body2" fontWeight={600}>{a.clientName}</Typography>
                          {!isWithoutQR && (
                            <>
                              <Typography variant="caption" color="text.secondary">
                                {a.startDate} &nbsp;|&nbsp; {a.time} &nbsp;|&nbsp; {a.reminder || "-"}
                              </Typography>
                            </>
                          )}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {!isWithoutQR && (
                            <IconButton
                              size="small"
                              onClick={() => setEditingAssignmentId(isOpen ? null : a.id)}
                              sx={{ p: 0.5 }}
                            >
                              {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteAssignment(a.id)}
                            disabled={updateAssignmentMutation.isLoading}
                            sx={{ p: 0.5 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Inline edit form - only for QR type */}
                      <Collapse in={!isWithoutQR && isOpen}>
                        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                          <Stack direction="row" spacing={1.5}>
                            <Box sx={{ flex: 1 }}>
                              <TextField
                                type="date"
                                fullWidth
                                size="small"
                                value={form.date || ""}
                                onChange={(e) => setForm({ date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                label="Date"
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <TextField
                                type="time"
                                fullWidth
                                size="small"
                                value={form.time || ""}
                                onChange={(e) => setForm({ time: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                label="Time"
                              />
                            </Box>
                          </Stack>

                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Repeat"
                            value={form.repeat || "Does not repeat"}
                            onChange={(e) => setForm({ repeat: e.target.value })}
                          >
                            {REPEAT_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </TextField>

                          <Stack direction="row" spacing={1.5}>
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              label="Repeat Every (hrs)"
                              value={form.repeatEvery || ""}
                              onChange={(e) => setForm({ repeatEvery: e.target.value })}
                              inputProps={{ min: 1 }}
                            />
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              label="Max Times / Day"
                              value={form.perday || ""}
                              onChange={(e) => setForm({ perday: e.target.value })}
                              inputProps={{ min: 1 }}
                            />
                          </Stack>

                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={!!form.noExpiry}
                                onChange={(e) => setForm({ noExpiry: e.target.checked })}
                              />
                            }
                            label={<Typography variant="body2">No expiry</Typography>}
                          />

                          {errMsg && <FormHelperText error>{errMsg}</FormHelperText>}

                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setEditingAssignmentId(null);
                                setReminderErrors((prev) => ({ ...prev, [a.id]: null }));
                              }}
                              sx={{ textTransform: "none" }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={updateAssignmentMutation.isLoading}
                              onClick={() =>
                                updateAssignmentMutation.mutate({
                                  assignmentId: a.id,
                                  startDate: form.date,
                                  time: form.time,
                                  repeat: form.repeat,
                                  repeatEvery: form.repeatEvery,
                                  perday: form.perday,
                                  noExpiry: form.noExpiry,
                                })
                              }
                              sx={{ textTransform: "none" }}
                            >
                              Save
                            </Button>
                          </Box>
                        </Stack>
                      </Collapse>
                    </Card>
                  );
                })}
              </Stack>
            </Grid>
          )}

          {/* Fields Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, mt: 1 }}>
              <Typography variant="h6" fontWeight={600}>Fields</Typography>
              <Button
                variant="outlined"
                onClick={() => append({ fieldName: "", fieldType: "TextBox", imageRequired: false, imageCount: 3, optionsText: "", videoLink: "" })}
                disabled={isLoadingChecklist}
                startIcon={<Iconify icon="eva:plus-outline" />}
                size="small"
                sx={{
                  textTransform: "none",
                  borderColor: "#2563EB",
                  color: "#2563EB",
                  "&:hover": { borderColor: "#1D4ED8", backgroundColor: "rgba(37, 99, 235, 0.04)" },
                }}
              >
                Add Field
              </Button>
            </Box>

            {fields.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4, bgcolor: "#F9FAFB", borderRadius: 2 }}>
                <Iconify icon="eva:file-text-outline" width={48} height={48} color="#9CA3AF" />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No fields added yet. Click "Add Field" to create checklist fields.
                </Typography>
              </Box>
            )}

            {fields.map((field, index) => {
              const fieldType = currentFields[index]?.fieldType;
              const hasOptions = needsOptions(fieldType);
              const isVideo = fieldType === "Embed Video";

              return (
                <Card
                  key={field.id || index}
                  variant="outlined"
                  sx={{ mb: 2, p: 2, backgroundColor: "#FAFAFA", position: "relative" }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <RHFTextField
                        name={`fields.${index}.fieldName`}
                        label="Field Name"
                        required
                        disabled={isLoadingChecklist}
                        placeholder="Enter field name"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <RHFSelect
                        name={`fields.${index}.fieldType`}
                        label="Field Type"
                        required
                        disabled={isLoadingChecklist}
                        onInputChange={(e) => {
                          setValue(`fields.${index}.optionsText`, "");
                        }}
                      >
                        {fieldTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </RHFSelect>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!watch(`fields.${index}.imageRequired`)}
                            onChange={(e) => setValue(`fields.${index}.imageRequired`, e.target.checked)}
                            disabled={isLoadingChecklist}
                            color="primary"
                          />
                        }
                        label="Image Required"
                      />
                    </Grid>

                    {hasOptions && (
                      <Grid item xs={12}>
                        <Controller
                          name={`fields.${index}.optionsText`}
                          control={control}
                          defaultValue=""
                          render={({ field: f }) => (
                            <>
                              <TextField
                                {...f}
                                value={f.value ?? ""}
                                fullWidth
                                size="small"
                                label="Values"
                                placeholder="e.g. Option1, Option2, Option3"
                                helperText="Multiple values comma separated"
                                disabled={isLoadingChecklist}
                              />
                              <FieldPreview fieldType={fieldType} optionsText={f.value} />
                            </>
                          )}
                        />
                      </Grid>
                    )}

                    {isVideo && (
                      <Grid item xs={12}>
                        <Controller
                          name={`fields.${index}.videoLink`}
                          control={control}
                          defaultValue=""
                          render={({ field: f }) => (
                            <>
                              <TextField
                                {...f}
                                value={f.value ?? ""}
                                fullWidth
                                size="small"
                                label="Video Link"
                                placeholder="https://youtube.com/..."
                                helperText="Only one video link allowed"
                                disabled={isLoadingChecklist}
                              />
                              <VideoPreview url={f.value} />
                            </>
                          )}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => remove(index)}
                          disabled={isLoadingChecklist}
                          startIcon={<Iconify icon="eva:trash-2-outline" />}
                          sx={{ textTransform: "none" }}
                        >
                          Remove Field
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              );
            })}
          </Grid>
        </Grid>
      </FormProvider>
    </FormWrapper>
  );
}